import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Monitor, Bell, Music, EyeOff, Eye, Copy, RefreshCw, Sparkles } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileUrl } from "../../components/site/HeroSlides";
import { VoicePick } from "../../components/invites/InviteExtras";

const box = "bg-white border border-[var(--line)] rounded-2xl p-5";

function AlertBox({ d, reload }) {
  const [text, setText] = useState("");
  const [minutes, setMinutes] = useState(5);
  const quick = ["First dance in 5 minutes", "Cake cutting now!", "Bouquet toss — single ladies to the floor", "Last dance!"];
  async function push(t) {
    try { await api.post("/weddings/mine/reception-mode/alert", { text: t, minutes }); toast.success("Alert is on the screen"); setText(""); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className={box} data-testid="reception-alert-box">
      <h3 className="font-serif text-xl mb-1 flex items-center gap-2"><Bell className="w-4 h-4 text-[var(--gold)]" /> Push an alert</h3>
      <p className="text-xs text-[var(--muted)] mb-3">Shows at the top of the big screen. Events from your schedule also alert 5 minutes before on their own.</p>
      {d.alert && <div className="mb-3 flex items-center justify-between rounded-xl bg-[var(--bg-warm)] px-3 py-2 text-sm" data-testid="reception-current-alert">
        <span>On screen: {d.alert.text}</span>
        <button onClick={async () => { try { await api.delete("/weddings/mine/reception-mode/alert"); reload(); } catch (e) { toast.error(formatApiError(e)); } }} className="text-xs underline" data-testid="reception-clear-alert">Clear</button>
      </div>}
      <div className="flex gap-2 mb-3">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={140} placeholder="Type an alert…" className="input-luxe flex-1" data-testid="reception-alert-input" />
        <select value={minutes} onChange={(e) => setMinutes(+e.target.value)} className="input-luxe !w-24" data-testid="reception-alert-minutes">
          {[2, 5, 10, 15, 30].map((m) => <option key={m} value={m}>{m} min</option>)}
        </select>
        <button disabled={!text.trim()} onClick={() => push(text.trim())} className="btn-gold text-xs" data-testid="reception-alert-send">Send</button>
      </div>
      <VoiceAlert text={text} minutesReload={reload} />
      <div className="flex flex-wrap gap-2">
        {quick.map((q, i) => <button key={q} onClick={() => push(q)} className="text-xs px-3 py-1.5 rounded-full border border-[var(--line)] hover:border-[var(--gold)]" data-testid={`reception-quick-${i}`}>{q}</button>)}
      </div>
    </div>
  );
}

function RevealBox({ rounds, reload }) {
  async function call(fn) { try { await fn(); reload(); } catch (e) { toast.error(formatApiError(e)); } }
  return (
    <div className={box} data-testid="reception-reveal-box">
      <h3 className="font-serif text-xl mb-1 flex items-center gap-2"><Music className="w-4 h-4 text-[var(--gold)]" /> First dance reveal</h3>
      <p className="text-xs text-[var(--muted)] mb-4">Hide the answer so guests just lock in a guess, watch the votes on screen, then reveal it. Right guesses get 10 points, and the fastest one gets 5 more.</p>
      {!rounds.length && <p className="text-sm text-[var(--muted)]">Create a "Guess the First Dance" game in Party Games first.</p>}
      {rounds.map((r) => (
        <div key={r.pg_id + r.round_id} className="border-t border-[var(--line)] py-3" data-testid={`reveal-round-${r.round_id}`}>
          <p className="font-medium text-sm">{r.prompt} <span className="text-xs text-[var(--muted)]">· {r.game_title} · {r.votes.reduce((a, b) => a + b, 0)} guesses</span></p>
          <p className="text-xs text-[var(--muted)] mb-2">Answer: {r.options[r.correct_index]}</p>
          {r.revealed ? (
            <div className="flex items-center gap-3 text-sm">
              <span className="text-emerald-700">Revealed · {r.winners.length} winners{r.fastest ? ` · fastest ${r.fastest}` : ""}</span>
              <button onClick={() => call(() => api.post("/weddings/mine/reception-mode/reveal/reset", { pg_id: r.pg_id, round_id: r.round_id }))} className="text-xs underline" data-testid={`reveal-reset-${r.round_id}`}>Reset</button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => call(() => api.put("/weddings/mine/reception-mode/hold", { pg_id: r.pg_id, round_id: r.round_id, held: !r.held }))} className="btn-outline-gold text-xs" data-testid={`reveal-hold-${r.round_id}`}>
                {r.held ? "Answer hidden ✓ (tap to show instantly)" : "Hide answer until reveal"}
              </button>
              <button onClick={() => call(async () => { const { data } = await api.post("/weddings/mine/reception-mode/reveal", { pg_id: r.pg_id, round_id: r.round_id }); toast.success(`Revealed: ${data.song}`); })} className="btn-gold text-xs" data-testid={`reveal-go-${r.round_id}`}>
                <Sparkles className="w-3 h-3" /> Reveal on screen
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PhotoBox({ photos, reload }) {
  return (
    <div className={box} data-testid="reception-photo-box">
      <h3 className="font-serif text-xl mb-1">Guest photos on screen</h3>
      <p className="text-xs text-[var(--muted)] mb-3">New guest photos go straight to the screen. Tap one to hide it.</p>
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
        {photos.map((p) => (
          <button key={p.item_id} onClick={async () => { try { await api.post(`/weddings/mine/reception-mode/photos/${p.item_id}/toggle`); reload(); } catch (e) { toast.error(formatApiError(e)); } }} className="relative aspect-square rounded-lg overflow-hidden" data-testid={`reception-photo-${p.item_id}`}>
            <img src={fileUrl(p.path)} alt="" className={`w-full h-full object-cover ${p.hidden ? "opacity-30 grayscale" : ""}`} />
            <span className="absolute top-1 right-1 bg-white/90 rounded-full p-1">{p.hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</span>
          </button>
        ))}
      </div>
      {!photos.length && <p className="text-sm text-[var(--muted)]">No guest photos yet.</p>}
    </div>
  );
}

export default function ReceptionModePage() {
  const [d, setD] = useState(null);
  const load = () => api.get("/weddings/mine/reception-mode").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []); // eslint-disable-line
  if (!d) return <div className="py-20"><div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto" /></div>;
  const url = `${window.location.origin}/reception-live/${d.slug}?k=${d.screen_key}`;
  return (
    <div data-testid="reception-mode-page" className="space-y-5">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Wedding day</p>
        <h1 className="font-serif font-light text-4xl">Live Reception Mode</h1>
        <p className="text-[var(--muted)] mt-2 text-sm">Open the big screen on a laptop or TV, then run it from your phone.</p>
      </div>
      <div className={`${box} flex flex-col sm:flex-row sm:items-center gap-3`}>
        <a href={url} target="_blank" rel="noreferrer" className="btn-gold text-xs" data-testid="reception-open-screen"><Monitor className="w-3 h-3" /> Open big screen</a>
        <button onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Screen link copied"), () => toast.error("Couldn't copy. Long-press the Open big screen button to copy the link."))} className="btn-outline-gold text-xs" data-testid="reception-copy-link"><Copy className="w-3 h-3" /> Copy screen link</button>
        <button onClick={async () => { try { await api.post("/weddings/mine/reception-mode/key"); toast.success("Old screen link turned off"); load(); } catch (e) { toast.error(formatApiError(e)); } }} className="text-xs text-[var(--muted)] inline-flex items-center gap-1" data-testid="reception-new-key"><RefreshCw className="w-3 h-3" /> New private link</button>
        <p className="text-xs text-[var(--muted)] sm:ml-auto">Keep this link private. It shows every guest photo.</p>
      </div>
      <AlertBox d={d} reload={load} />
      <RevealBox rounds={d.rounds} reload={load} />
      <PhotoBox photos={d.photos} reload={load} />
    </div>
  );
}

function VoiceAlert({ text, minutesReload }) {
  const [voices, setVoices] = useState(null);
  const [vid, setVid] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/voice/voices").then((r) => { setVoices(r.data); setVid(r.data[0]?.voice_id || ""); }).catch(() => setVoices([])); }, []);
  if (!voices) return null;
  if (!voices.length) return <p className="mb-3 text-xs text-[var(--muted)]" data-testid="reception-voice-off">Spoken announcements are taking a short break. Please try again a little later.</p>;
  async function go() {
    if (!text.trim()) { toast.error("Type the announcement first"); return; }
    setBusy(true);
    try { await api.post("/weddings/mine/reception-mode/voice-alert", { voice_id: vid, text }); toast.success("Announcement is playing on the big screen"); minutesReload(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  return <div className="mb-3"><VoicePick voices={voices} vid={vid} setVid={setVid} onGo={go} busy={busy} label="Say it out loud" testid="reception-voice" /></div>;
}
